import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "分类", to: "/category" },
  { label: "观影指南", to: "/guide" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setMobileSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileSearchOpen) {
      mobileInputRef.current?.focus();
    }
  }, [mobileSearchOpen]);

  const runSearch = (value: string) => {
    const q = value.trim();
    navigate(q ? `/category?q=${encodeURIComponent(q)}` : "/category");
    setKeyword("");
    setMobileSearchOpen(false);
    setMenuOpen(false);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    runSearch(keyword);
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/70 bg-background-50/90 backdrop-blur-md">
      <div className="w-full px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-3 md:gap-6">
          <div className="flex items-center gap-6 lg:gap-9">
            <Link
              to="/"
              className="flex items-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50">
                <i className="ri-heart-3-fill text-base"></i>
              </span>
              <span className="font-heading text-base md:text-lg font-semibold tracking-wide text-foreground-950">
                适合夫妻二人看的影片
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors cursor-pointer ${
                    isActive(item.to)
                      ? "bg-primary-100 text-primary-800 font-medium"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          <form
            onSubmit={handleSubmit}
            className="hidden md:flex items-center gap-2 rounded-full border border-background-300/70 bg-background-100/70 px-3 py-1.5 focus-within:border-primary-400"
          >
            <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片名称或关键词"
              aria-label="搜索影片"
              className="w-36 lg:w-52 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 outline-none"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              搜索
            </button>
          </form>

          <button
            type="button"
            aria-label="搜索"
            onClick={() => {
              setMobileSearchOpen((v) => !v);
              setMenuOpen(false);
            }}
            className="md:hidden flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-search-line text-lg"></i>
          </button>

          <button
            type="button"
            aria-label="菜单"
            onClick={() => {
              setMenuOpen((v) => !v);
              setMobileSearchOpen(false);
            }}
            className="md:hidden flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>

        {mobileSearchOpen && (
          <div className="md:hidden pb-3">
            <form
              onSubmit={handleSubmit}
              className="flex items-center gap-2 rounded-full border border-background-300/70 bg-background-100/70 px-3 py-2 focus-within:border-primary-400"
            >
              <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                ref={mobileInputRef}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片名称或关键词"
                aria-label="搜索影片"
                className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 outline-none"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>
        )}

        {menuOpen && (
          <nav className="md:hidden pb-3 flex flex-col gap-1 animate-soft-fade">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-2.5 text-sm cursor-pointer ${
                  isActive(item.to)
                    ? "bg-primary-100 text-primary-800 font-medium"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
}